import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  BoxesIcon,
  CheckCircle2Icon,
  ExternalLinkIcon,
  GaugeIcon,
  GlobeIcon,
  MoreHorizontalIcon,
  PackageIcon,
  PrinterIcon,
  ShoppingCartIcon,
  StickyNoteIcon,
  StoreIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CurrentVehicleNotice } from "@/components/app/current-vehicle";
import { EmptyState } from "@/components/app/empty-state";
import { FileImage } from "@/components/app/file-image";
import { Page, PageHeader } from "@/components/app/page";
import { QuantityInput } from "@/components/app/qty";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/misc";
import { Checkbox } from "@/components/ui/switch";
import { AddToOrderDialog } from "@/features/orders/add-to-order-dialog";
import { QuickPurchaseDialog } from "@/features/orders/quick-purchase-dialog";
import { PartsListDialog, type PartsListItem } from "@/features/parts-list/parts-list-dialog";
import { useCurrentVehicle } from "@/lib/current-vehicle";
import { openExternal } from "@/lib/desktop";
import { useFormat } from "@/lib/format";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/to-order")({
  component: ToOrderPage,
});

type Group = RouterOutputs["orders"]["toOrder"]["groups"][number];
type Item = Group["items"][number];

function ToOrderPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { vehicleId } = useCurrentVehicle();
  const { data, isLoading } = useQuery(trpc.orders.toOrder.queryOptions({ vehicleId }));
  // Per part: chosen amount and whether it's selected. Defaults come from the suggestion.
  const [overrides, setOverrides] = useState<Record<string, { qty?: number; selected?: boolean }>>(
    {},
  );
  const add = useMutation(trpc.orders.addToDraft.mutationOptions());
  const [dialog, setDialog] = useState<"list" | "order" | "buy" | null>(null);
  // A parts list for one supplier group, or for the whole page when null.
  const [listGroup, setListGroup] = useState<Group | null>(null);

  const items = useMemo(() => data?.groups.flatMap((g) => g.items) ?? [], [data]);
  const qtyOf = (item: Item) => overrides[item.partId]?.qty ?? (item.suggestedQty || item.toOrder);
  const selectedOf = (item: Item) => overrides[item.partId]?.selected ?? item.suggestedQty > 0;
  const selected = items.filter((i) => selectedOf(i) && qtyOf(i) > 0);
  const singleWorkOrder = (item: Item) =>
    item.workOrders.length === 1 ? (item.workOrders[0]?.id ?? null) : null;

  /** The parts list starts with what is selected on the page, or in the group it was opened from. */
  const listItems = (): PartsListItem[] => {
    const inGroup = listGroup ? new Set(listGroup.items.map((i) => i.partId)) : null;
    const pick = (item: Item) => selectedOf(item) && qtyOf(item) > 0;
    const groupPicked = listGroup?.items.some(pick) ?? true;
    return items.map((item) => ({
      partId: item.partId,
      name: item.name,
      unit: item.unit,
      primaryNumber: item.primaryNumber,
      qty: qtyOf(item),
      workOrderIds: item.workOrders.map((w) => w.id),
      hint: item.workOrders.map((w) => `${w.title} · ${w.vehicleName}`).join(", ") || null,
      selected: inGroup ? inGroup.has(item.partId) && (!groupPicked || pick(item)) : pick(item),
    }));
  };

  const submit = (list: Item[]) => {
    if (!list.length) return;
    add.mutate(
      {
        orderId: null,
        draftName: t("orders.newDraftName"),
        items: list.map((item) => ({
          partId: item.partId,
          qty: qtyOf(item),
          workOrderId: singleWorkOrder(item),
        })),
      },
      {
        onSuccess: (result) => {
          setOverrides({});
          const first = result.orders[0];
          toast.success(
            result.orders.length === 1 && first
              ? t("orders.addedToOrders", { count: 1, name: first.name })
              : t("orders.addedToOrders", { count: result.orders.length, name: "" }),
            first
              ? {
                  action: {
                    label: t("common.view"),
                    onClick: () =>
                      void navigate({ to: "/orders/$orderId", params: { orderId: first.id } }),
                  },
                }
              : undefined,
          );
        },
      },
    );
  };

  return (
    <Page>
      <PageHeader
        title={t("toOrder.title")}
        subtitle={t("toOrder.subtitle")}
        actions={
          items.length ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setListGroup(null);
                  setDialog("list");
                }}
              >
                <PrinterIcon />
                {t("partsList.print")}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label={t("common.more")}>
                    <MoreHorizontalIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled={!selected.length} onSelect={() => setDialog("order")}>
                    <ShoppingCartIcon />
                    {t("orders.addToOrderTitle")}…
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={!selected.length} onSelect={() => setDialog("buy")}>
                    <StoreIcon />
                    {t("orders.quickPurchase")}…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={() => submit(selected)} disabled={!selected.length || add.isPending}>
                <ShoppingCartIcon />
                {t("toOrder.addSelected")}
                {selected.length ? (
                  <span className="rounded-full bg-primary-foreground/20 px-1.5 text-xs tabular">
                    {selected.length}
                  </span>
                ) : null}
              </Button>
            </>
          ) : null
        }
      />
      <CurrentVehicleNotice />
      {isLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : !data?.groups.length ? (
        <EmptyState
          icon={CheckCircle2Icon}
          title={t("toOrder.emptyTitle")}
          body={t("toOrder.emptyBody")}
        />
      ) : (
        <div className="flex flex-col gap-6">
          {data.groups.map((group) => {
            const groupSelected = group.items.filter((i) => selectedOf(i) && qtyOf(i) > 0);
            const allSelected = group.items.every((i) => selectedOf(i));
            return (
              <Card key={group.supplier?.id ?? "none"} className="overflow-hidden">
                <div className="flex items-center gap-3 border-b border-border bg-muted/40 px-4 py-2.5">
                  <Checkbox
                    aria-label={t("toOrder.selectAll")}
                    checked={allSelected ? true : groupSelected.length ? "indeterminate" : false}
                    onCheckedChange={(checked) =>
                      setOverrides((current) => {
                        const next = { ...current };
                        for (const item of group.items)
                          next[item.partId] = { ...next[item.partId], selected: checked === true };
                        return next;
                      })
                    }
                  />
                  <SupplierKindIcon kind={group.supplier?.kind ?? null} />
                  <span className="min-w-0 flex-1 truncate text-sm">
                    <span className="font-semibold">
                      {group.supplier?.name ?? t("toOrder.noSupplier")}
                    </span>
                    {group.supplier ? (
                      <span className="text-muted-foreground">
                        {" · "}
                        {group.supplier.kind === "local"
                          ? t("settings.supplierLocal")
                          : t("settings.supplierOnline")}
                      </span>
                    ) : null}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("partsList.print")}
                    onClick={() => {
                      setListGroup(group);
                      setDialog("list");
                    }}
                  >
                    <PrinterIcon />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!groupSelected.length || add.isPending}
                    onClick={() => submit(groupSelected)}
                  >
                    <ShoppingCartIcon />
                    {t("toOrder.addGroup")}
                  </Button>
                </div>
                <ul className="divide-y divide-border">
                  {group.items.map((item) => (
                    <ToOrderRow
                      key={item.partId}
                      item={item}
                      qty={qtyOf(item)}
                      selected={selectedOf(item)}
                      onQty={(qty) =>
                        setOverrides((c) => ({
                          ...c,
                          [item.partId]: { ...c[item.partId], qty, selected: qty > 0 },
                        }))
                      }
                      onSelected={(value) =>
                        setOverrides((c) => ({
                          ...c,
                          [item.partId]: { ...c[item.partId], selected: value },
                        }))
                      }
                    />
                  ))}
                </ul>
              </Card>
            );
          })}
        </div>
      )}
      <PartsListDialog
        open={dialog === "list"}
        onOpenChange={(open) => !open && setDialog(null)}
        items={dialog === "list" ? listItems() : []}
        dealerId={listGroup?.supplier?.kind === "local" ? listGroup.supplier.id : null}
      />
      <AddToOrderDialog
        open={dialog === "order"}
        onOpenChange={(open) => !open && setDialog(null)}
        items={selected.map((item) => ({
          partId: item.partId,
          name: item.name,
          unit: item.unit,
          qty: qtyOf(item),
          workOrderId: singleWorkOrder(item),
        }))}
      />
      <QuickPurchaseDialog
        open={dialog === "buy"}
        onOpenChange={(open) => !open && setDialog(null)}
        initialLines={selected.map((item) => ({
          partId: item.partId,
          name: item.name,
          unit: item.unit,
          qty: qtyOf(item),
        }))}
      />
    </Page>
  );
}

/** A supplier group's icon: a local dealer's counter, an online shop, or no supplier yet. */
function SupplierKindIcon({ kind }: { kind: "online" | "local" | null }) {
  const Icon = kind === "local" ? StoreIcon : kind === "online" ? GlobeIcon : PackageIcon;
  return <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />;
}

function ToOrderRow({
  item,
  qty,
  selected,
  onQty,
  onSelected,
}: {
  item: Item;
  qty: number;
  selected: boolean;
  onQty: (qty: number) => void;
  onSelected: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const covered = item.suggestedQty === 0;
  return (
    <li
      className={cn(
        "flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center",
        covered && !selected && "bg-muted/40",
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <Checkbox
          className="mt-3"
          checked={selected}
          onCheckedChange={(checked) => onSelected(checked === true)}
          aria-label={item.name}
        />
        <FileImage fileId={item.coverFileId} alt="" icon={BoxesIcon} className="size-10" />
        <div className="min-w-0 flex-1">
          <Link
            to="/inventory/$partId"
            params={{ partId: item.partId }}
            className="block truncate font-medium hover:underline"
          >
            {item.name}
          </Link>
          {item.primaryNumber ? (
            <div className="truncate font-mono text-xs text-muted-foreground">
              {item.primaryNumber}
            </div>
          ) : null}
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Badge tone="missing">
              {t("stockStatus.toOrder", { qty: format.qty(item.toOrder, item.unit) })}
            </Badge>
            {item.workOrders.map((wo) => (
              <Link key={wo.id} to="/work-orders/$workOrderId" params={{ workOrderId: wo.id }}>
                <Badge tone="outline" className="hover:bg-muted">
                  {wo.title} · {wo.vehicleName}
                </Badge>
              </Link>
            ))}
            {item.belowMinimum ? (
              <Badge tone="partial">
                <GaugeIcon />
                {t("toOrder.belowMinimum")}
              </Badge>
            ) : null}
            {item.inDraft > 0 ? (
              <Badge tone="neutral">
                <StickyNoteIcon />
                {covered
                  ? t("toOrder.covered")
                  : t("toOrder.alreadyInDraft", { qty: format.qty(item.inDraft, item.unit) })}
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 pl-[76px] sm:pl-0">
        {item.source?.priceMinor !== null && item.source?.priceMinor !== undefined ? (
          <span className="hidden w-20 text-right text-xs text-muted-foreground tabular md:block">
            {format.money(item.source.priceMinor, item.source.currency)}
          </span>
        ) : null}
        <QuantityInput
          className="w-36"
          aria-label={t("common.quantity")}
          unit={item.unit}
          value={qty}
          onChange={(v) => onQty(v ?? 0)}
        />
        {item.source?.url ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("parts.openShop")}
            onClick={() => openExternal(item.source?.url as string)}
          >
            <ExternalLinkIcon />
          </Button>
        ) : (
          <span className="w-8" />
        )}
      </div>
    </li>
  );
}
